import * as React from 'react'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'

import {
  ListItemText,
  Card,
  CardHeader,
  CardContent,
  Tooltip,
  Avatar,
  Icon,
  Menu,
  MenuItem,
  Button,
  Box,
  styled,
  useTheme,
} from '@design/pt-ui'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/free-solid-svg-icons'

import getFormatIcon from './getFormatIcon'
import DI from '../../app/di'
import { ICreativeService } from '../../interfaces/services/creatives'
import { FormatsetId } from '../../interfaces/entities/formatset'
import { Project } from '../../interfaces/entities/project'

import { State } from '../../model'
import * as actions from '../../model/creative/actions'
import { translateWithUserLocale } from '../../utils/i18n'
import { toast } from 'react-toastify'
import { Labeled } from '../../interfaces/base'

import CreativesImport from '../../pages/ProjectView/CreativesImport'
import { IStringAnyMap } from '../../types/map'

const Root = styled('div')({
  '&:hover': {
    cursor: 'pointer',
  },
})

const StyledCard = styled(Card)({
  maxWidth: 200,
})

const StyledAvatar = styled(Avatar)({
  width: '30px !important',
  height: '30px !important',
})

const StyledCardHeader = styled(CardHeader)(({ theme }) => ({
  paddingTop: theme.spacing(1),
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  paddingBottom: theme.spacing(1),
}))

const FormatIcon = styled(Icon)(({ theme }) => ({
  marginRight: theme.spacing(1.3),
  color: theme.palette.custom.gray.light,
}))

const FormatRow = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  flexFlow: 'row',
  margin: '10px 0',
  justifyContent: 'flex-start',
  alignItems: 'center',
  color: 'grey',
  fontSize: 16,
})

const StyledCardContent = styled(CardContent, {
  shouldForwardProp: (prop) => prop !== 'warn',
})<{ warn?: boolean }>(({ warn }) => ({
  padding: '4px !important',
  height: warn ? 120 : 128,
}))

const CardNameMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: '6px 8px !important',
  fontSize: '0.9rem',
  '&:hover': {
    backgroundColor: theme.palette.custom.gray.light,
  },
})) as typeof MenuItem

const EmptyMedia = styled('div')({
  width: 200,
  minHeight: 120,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

const PlusIcon = styled(FontAwesomeIcon)({
  fontSize: 70,
  color: '#b7b7b7',
})

const StyledFormatMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: theme.spacing(1),
  paddingLeft: 8,
  paddingRight: 12,
})) as typeof MenuItem

const StyledListItemText = styled(ListItemText)({
  paddingLeft: 5,
  '& .MuiListItemText-primary': {
    fontWeight: 400,
  },
})

const AddCreativeIcon = styled(Icon)(({ theme }) => ({
  marginRight: theme.spacing(0.5),
}))

const OverInfo = styled('div')(({ theme }) => ({
  color: theme.palette.custom.link.main,
  textAlign: 'center',
  fontWeight: 700,
  fontSize: 13,
}))

interface IProps {
  project: Project
  formats: Labeled[]
  formatsetId: FormatsetId
  formatsetName: string
  formatsetPlatform: string
  formatsetData: IStringAnyMap
  warn: boolean
  makingCreative: boolean
  makeCreative: typeof actions.makeCreative
  makeCreativeComplete: typeof actions.makeCreativeComplete
}

interface IState {
  anchorEl: any
  importCreativesDialogOpened: boolean
}

interface IAddFormatProps {
  formatsetId: FormatsetId
  formatsetName: string
  formatsetPlatform: string
  format: Labeled

  makingCreative: boolean
  makeCreative: typeof actions.makeCreative
  makeCreativeComplete: typeof actions.makeCreativeComplete

  onAdd?: () => any
  icon?: React.ReactNode
  title?: string
}

type AddCreativeProps = {
  formatsetId: FormatsetId
  formatsetName: string
  formatsetPlatform: string
  format: Labeled
  makingCreative: boolean
  makeCreative: typeof actions.makeCreative
  makeCreativeComplete: typeof actions.makeCreativeComplete
}

const addCreative = async (opts: AddCreativeProps) => {
  const {
    formatsetId,
    formatsetName,
    format,
    makingCreative,
    makeCreative,
    makeCreativeComplete,
  } = opts
  if (makingCreative) {
    return
  }

  makeCreative()
  const creativeService = DI.get('CreativeService') as ICreativeService

  const response = await creativeService.createCreative({
    formatsetId: formatsetId,
    formatsetName: formatsetName,
    creativeType: format.name,
  })
  makeCreativeComplete()

  if (response.type === 'error') {
    toast.error(translateWithUserLocale('error.make_creative'))
    return
  } else {
    const history = DI.get('history')
    history.push(`/creatives/${response.payload.id}`)
  }
}

function AddFormat(props: IAddFormatProps) {
  const handleCreativeAdd = async () => {
    if (props.onAdd) {
      props.onAdd()
    }

    return addCreative({
      formatsetId: props.formatsetId,
      formatsetName: props.formatsetName,
      format: props.format,
      formatsetPlatform: props.formatsetPlatform,
      makingCreative: props.makingCreative,
      makeCreative: props.makeCreative,
      makeCreativeComplete: props.makeCreativeComplete,
    })
  }

  const { format } = props

  return (
    <StyledFormatMenuItem component="div" onClick={handleCreativeAdd}>
      {props.icon ? (
        props.icon
      ) : (
        <FormatIcon key={format.name}>{getFormatIcon(format.name)}</FormatIcon>
      )}
      <StyledListItemText primary={props.title ? props.title : format.label} />
    </StyledFormatMenuItem>
  )
}

function CreativeCard(props: IProps) {
  const theme = useTheme()
  const [state, setState] = React.useState<IState>({
    anchorEl: null,
    importCreativesDialogOpened: false,
  })

  const handleClick = (event: any) => {
    handleClose()
    if (props.formats.length > 1) {
      setState({
        anchorEl: event.currentTarget,
        importCreativesDialogOpened: false,
      })
    } else {
      addCreative({
        formatsetId: props.formatsetId,
        formatsetName: props.formatsetName,
        formatsetPlatform: props.formatsetPlatform,
        format: props.formats[0],
        makingCreative: props.makingCreative,
        makeCreative: props.makeCreative,
        makeCreativeComplete: props.makeCreativeComplete,
      })
    }
  }

  const handleClose = () => {
    setState((state) => ({ ...state, anchorEl: null }))
  }

  const openImportCreativesDialog = () => {
    setState((state) => ({
      ...state,
      importCreativesDialogOpened: true,
    }))
  }

  const closeImportCreativesDialog = () => {
    setState((state) => ({
      ...state,
      importCreativesDialogOpened: false,
    }))
  }

  if (props.formats.length === 0) {
    return null
  }

  const { anchorEl } = state

  const style = props.warn
    ? {
        boxShadow: `0px 0px 1px 2px ${theme.palette.custom.border.recommended}`,
      }
    : {}

  function getWarnMessage() {
    let message = ''
    switch (props.formatsetPlatform) {
      case 'facebook':
        message = translateWithUserLocale('warning.adding_above_recommended')
        break
      case 'performance_advertising':
        message = translateWithUserLocale(
          'warning.add_more_creatives_reach_recommended',
        )
        break
    }
    return message
  }

  return (
    <Root>
      <StyledCard sx={style}>
        <StyledCardHeader
          avatar={
            <Tooltip title={''} placement="top">
              <StyledAvatar
                alt=""
                aria-label="Check"
                sx={{ backgroundColor: theme.palette.custom.gray.light }}
              />
            </Tooltip>
          }
        />
        <Tooltip
          title={
            props.formatsetPlatform === 'facebook' && props.warn
              ? translateWithUserLocale('warning.more_than_recommended')
              : ''
          }
          placement="top">
          <Box>
            <EmptyMedia onClick={handleClick}>
              <PlusIcon icon={faPlus} />
            </EmptyMedia>
            {props.warn ? <OverInfo>{getWarnMessage()}</OverInfo> : ''}
          </Box>
        </Tooltip>

        <StyledCardContent warn={props.warn}>
          {props.formats.length === 1 ? (
            <FormatRow>
              <FormatIcon>{getFormatIcon(props.formats[0].name)}</FormatIcon>
              {props.formats[0].label}
            </FormatRow>
          ) : (
            <FormatRow>
              {props.formats.map((format) => {
                return (
                  <Tooltip
                    key={format.name}
                    title={format.label}
                    placement="top">
                    <FormatIcon>{getFormatIcon(format.name)}</FormatIcon>
                  </Tooltip>
                )
              })}
            </FormatRow>
          )}
          {props.formats.length === 1 ? (
            <>
              <AddFormat
                formatsetId={props.formatsetId}
                formatsetName={props.formatsetName}
                formatsetPlatform={props.formatsetPlatform}
                format={props.formats[0]}
                icon={<AddCreativeIcon>add_box</AddCreativeIcon>}
                makingCreative={props.makingCreative}
                makeCreative={props.makeCreative}
                makeCreativeComplete={props.makeCreativeComplete}
                title={translateWithUserLocale('card.add_creative')}
              />

              <CreativesImport
                formats={props.formats}
                formatsetId={props.formatsetId}
                formatsetName={props.formatsetName}
                formatsetPlatform={props.formatsetPlatform}
                formatsetData={props.formatsetData}
                open={state.importCreativesDialogOpened}
                onOpen={openImportCreativesDialog}
                onClose={closeImportCreativesDialog}
                project={props.project}
                activator={
                  <Button size={'small'} color="inherit">
                    <Icon sx={{ mr: '5px' }}>library_add</Icon>
                    <span style={{ fontWeight: 'normal', fontSize: '0.9rem' }}>
                      {translateWithUserLocale('card.import_creative(s)')}
                    </span>
                  </Button>
                }
                onCloseDialog={closeImportCreativesDialog}
              />
            </>
          ) : (
            <>
              <CardNameMenuItem onClick={handleClick}>
                <AddCreativeIcon>add_box</AddCreativeIcon>
                {translateWithUserLocale('card.add_creative')}
              </CardNameMenuItem>
              <Menu
                keepMounted={true}
                anchorEl={anchorEl}
                open={anchorEl != null}
                onClose={handleClose}>
                {props.formats.map((format, i) => {
                  return (
                    <MenuItem key={i}>
                      <AddFormat
                        formatsetId={props.formatsetId}
                        formatsetName={props.formatsetName}
                        formatsetPlatform={props.formatsetPlatform}
                        format={format}
                        key={format.name}
                        makingCreative={props.makingCreative}
                        makeCreative={props.makeCreative}
                        makeCreativeComplete={props.makeCreativeComplete}
                        onAdd={handleClose}
                      />
                    </MenuItem>
                  )
                })}
              </Menu>
              <CreativesImport
                formats={props.formats}
                formatsetId={props.formatsetId}
                formatsetName={props.formatsetName}
                formatsetPlatform={props.formatsetPlatform}
                formatsetData={props.formatsetData}
                open={state.importCreativesDialogOpened}
                onOpen={openImportCreativesDialog}
                onClose={closeImportCreativesDialog}
                project={props.project}
                activator={
                  <Button
                    size={'small'}
                    color="inherit"
                    sx={{
                      width: '100%',
                      justifyContent: 'flex-start',
                    }}>
                    <Icon sx={{ mr: '5px' }}>library_add</Icon>
                    <span style={{ fontWeight: 'normal', fontSize: '0.9rem' }}>
                      {translateWithUserLocale('card.import_creative(s)')}
                    </span>
                  </Button>
                }
                onCloseDialog={closeImportCreativesDialog}
              />
            </>
          )}
        </StyledCardContent>
      </StyledCard>
    </Root>
  )
}

const mapStateToProps = (state: State) => {
  return {
    makingCreative: state.creative.making,
  }
}

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    makeCreative: () => dispatch(actions.makeCreative()),
    makeCreativeComplete: () => dispatch(actions.makeCreativeComplete()),
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CreativeCard as any) as React.ComponentType<any>
